import { GanttTimelineRange } from './gantt-timeline-range';
import { GanttStatic, ZoomLevel } from '../../../../assets/js/codebase/dhtmlxgantt';
import { GanttZoomOptions } from './gantt.types';

export function configureGanttZoom(gantt: GanttStatic, options: GanttZoomOptions, rememberedLevel?: number | string, range?: GanttTimelineRange): void {
    const levels = options.levels.length ? options.levels : createDefaultGanttZoomLevels(gantt);
    const activeLevel = normalizeGanttZoomLevel(rememberedLevel ?? options.activeLevel, levels);

    gantt.ext.zoom.init({
        levels,
        activeLevelIndex: resolveZoomLevelIndex(levels, activeLevel),
        trigger: options.trigger,
        handler: options.trigger === 'wheel' ? createWheelZoomHandler(gantt, options.useKey, range) : undefined,
        useKey: options.useKey,
        widthStep: options.widthStep,
        minColumnWidth: options.minColumnWidth,
        maxColumnWidth: options.maxColumnWidth,
        element: () => gantt.$task,
    });
}

function createWheelZoomHandler(gantt: GanttStatic, useKey: GanttZoomOptions['useKey'], range?: GanttTimelineRange): (event: Event) => void {
    return (event: Event) => {
        const wheelEvent = event as WheelEvent;

        if (typeof useKey === 'string' && !Boolean(wheelEvent[useKey as keyof WheelEvent])) {
            return;
        }

        const legacyEvent = wheelEvent as WheelEvent & { wheelDeltaY?: number; wheelDelta?: number };
        const deltaY = wheelEvent.deltaY || -(legacyEvent.wheelDeltaY ?? legacyEvent.wheelDelta ?? 0);
        if (!deltaY || !gantt.$task) return;

        // Programmatic zoom has no cursor position. Keep the pointed date
        // at the cursor instead, accounting for the current horizontal scroll.
        const timeline = gantt.$task;
        const offset = wheelEvent.clientX - timeline.getBoundingClientRect().left - timeline.clientLeft;
        const scroll = gantt.getScrollState();
        const date = offset >= 0 && offset <= timeline.clientWidth
            ? gantt.dateFromPos(scroll.x + offset) : undefined;
        const level = gantt.ext.zoom.getCurrentLevel();

        range?.anchor(date);
        wheelEvent.preventDefault();
        wheelEvent.stopPropagation();

        // Providers may list levels in either direction. Wheel-up must choose
        // the finer level rather than relying on the native index convention.
        const levels = gantt.ext.zoom.getLevels();
        const resolution = (item: ZoomLevel): number => {
            const scale = item.scales[item.scales.length - 1];
            const reference = date ?? new Date(2020, 0, 15);
            return (gantt.date.add(reference, scale.step ?? 1, scale.unit).getTime() - reference.getTime())
                / (item.min_column_width ?? gantt.config.min_column_width);
        };
        const direction = resolution(levels[0]) > resolution(levels[levels.length - 1]) ? 1 : -1;
        const nextLevel = level + (deltaY < 0 ? direction : -direction);
        if (nextLevel >= 0 && nextLevel < levels.length) gantt.ext.zoom.setLevel(nextLevel);
        else range?.anchor(undefined);

        if (date && gantt.ext.zoom.getCurrentLevel() !== level) {
            const nextOffset = wheelEvent.clientX - gantt.$task.getBoundingClientRect().left - gantt.$task.clientLeft;
            gantt.scrollTo(Math.max(0, gantt.posFromDate(date) - nextOffset), scroll.y);
        }
    };
}

export function getCurrentGanttZoomLevel(gantt: GanttStatic): number | string {
    const currentLevelIndex = gantt.ext.zoom.getCurrentLevel();
    const currentLevel = gantt.ext.zoom.getLevels()[currentLevelIndex];

    return currentLevel?.name ?? currentLevelIndex;
}

export function normalizeGanttZoomLevel(level: number | string, levels: ZoomLevel[]): number | string {
    if (typeof level !== 'number' || !isDefaultSmoothZoomLevels(levels)) {
        return level;
    }

    const legacyLevelNames = ['year', 'quarter', 'month', 'week', 'day', 'hour'];

    return legacyLevelNames[level] ?? level;
}

function resolveZoomLevelIndex(levels: ZoomLevel[], level: number | string): number {
    if (typeof level === 'number') {
        return level >= 0 && level < levels.length ? level : 0;
    }

    const index = levels.findIndex((item) => item.name === level);

    return index >= 0 ? index : 0;
}

function isDefaultSmoothZoomLevels(levels: ZoomLevel[]): boolean {
    const levelNames = new Set(levels.map((level) => level.name));

    return levelNames.has('half-year') && levelNames.has('two-week') && levelNames.has('half-day') && levelNames.has('half-hour');
}

function createDefaultGanttZoomLevels(gantt: GanttStatic): ZoomLevel[] {
    return [
        {
            name: 'year',
            scale_height: 60,
            min_column_width: 50,
            scales: [
                { unit: 'year', step: 1, format: '%Y' },
                { unit: 'month', step: 1, format: '%M' },
            ],
        },
        {
            name: 'half-year',
            scale_height: 55,
            min_column_width: 70,
            scales: [
                { unit: 'year', step: 1, format: '%Y' },
                {
                    unit: 'quarter',
                    step: 1,
                    format: (date: Date) => `Q${Math.floor(date.getMonth() / 3) + 1}`,
                },
            ],
        },
        {
            name: 'quarter',
            scale_height: 50,
            min_column_width: 75,
            scales: [
                {
                    unit: 'quarter',
                    step: 1,
                    format: (date: Date) => `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`,
                },
                { unit: 'month', step: 1, format: '%M' },
            ],
        },
        {
            name: 'month',
            scale_height: 50,
            min_column_width: 90,
            scales: [
                { unit: 'month', step: 1, format: '%F %Y' },
                { unit: 'week', step: 1, format: 'W%W' },
            ],
        },
        {
            name: 'two-week',
            scale_height: 50,
            min_column_width: 70,
            scales: [
                { unit: 'month', step: 1, format: '%F %Y' },
                { unit: 'week', step: 1, format: 'W%W' },
                { unit: 'day', step: 2, format: '%d' },
            ],
        },
        {
            name: 'week',
            scale_height: 50,
            min_column_width: 55,
            scales: [
                {
                    unit: 'week',
                    step: 1,
                    format: (date: Date) => {
                        const dateToStr = gantt.date.date_to_str('%d %M');
                        const endDate = gantt.date.add(date, 6, 'day');
                        return `(Uke ${gantt.date.date_to_str('%W')(date)}) ${dateToStr(date)} - ${dateToStr(endDate)}`;
                    },
                },
                { unit: 'day', step: 1, format: '%D %d' },
            ],
        },
        {
            name: 'day',
            scale_height: 50,
            min_column_width: 70,
            scales: [
                { unit: 'day', step: 1, format: '%d %M' },
                { unit: 'hour', step: 6, format: '%H:%i' },
            ],
        },
        {
            name: 'half-day',
            scale_height: 50,
            min_column_width: 60,
            scales: [
                { unit: 'day', step: 1, format: '%d %M %Y' },
                { unit: 'hour', step: 2, format: '%H:%i' },
            ],
        },
        {
            name: 'hour',
            scale_height: 50,
            min_column_width: 55,
            scales: [
                { unit: 'day', step: 1, format: '%d %M %Y' },
                { unit: 'hour', step: 1, format: '%H:%i' },
            ],
        },
        {
            name: 'half-hour',
            scale_height: 50,
            min_column_width: 45,
            scales: [
                { unit: 'day', step: 1, format: '%d %M %Y' },
                { unit: 'hour', step: 1, format: '%H:%i' },
                { unit: 'minute', step: 30, format: '%i' },
            ],
        },
    ];
}
